import React from 'react';

import { OrgUnit, collectExpandable, findUnit } from '../types';

export interface UnitTreeView {
  /** Puts keyboard focus back on a unit's row. */
  focusRow: (unitId: string | null) => void;
  expandedIds: Set<string>;
  toggleExpand: (unitId: string) => void;
  expand: (unitId: string) => void;
  selectedId: string | null;
  selected: OrgUnit | null;
  select: (unitId: string | null) => void;
  /** Closes the panel and puts focus back on the row it was showing. */
  closePanel: () => void;
  /** Wraps the tree and the panel, so Esc only acts while focus is inside them. */
  regionRef: React.RefObject<HTMLDivElement>;
  onRegionKeyDown: (event: React.KeyboardEvent) => void;
}

const toggled = (ids: Set<string>, unitId: string): Set<string> => {
  const next = new Set(ids);
  if (next.has(unitId)) next.delete(unitId);
  else next.add(unitId);
  return next;
};

export const useUnitTreeView = (units: OrgUnit[]): UnitTreeView => {
  const [expandedIds, setExpandedIds] = React.useState<Set<string>>(() => collectExpandable(units));

  // The tree is empty on the first render, so there is nothing to expand until it loads.
  const seeded = React.useRef(units.length > 0);
  React.useEffect(() => {
    if (!seeded.current && units.length) {
      seeded.current = true;
      setExpandedIds(collectExpandable(units));
    }
  }, [units]);
  // undefined: not chosen yet, so the top unit. null: the panel was closed.
  const [chosenId, setChosenId] = React.useState<string | null | undefined>(undefined);
  const regionRef = React.useRef<HTMLDivElement>(null);

  // A chosen unit can disappear — deleted, or carried off with a deleted parent — and the
  // panel would then show nothing. Fall back to the top unit.
  const chosenStillExists = chosenId ? !!findUnit(units, chosenId) : true;
  const selectedId = chosenId === undefined || (chosenId && !chosenStillExists) ? (units[0]?.uuid ?? null) : chosenId;
  const selected = selectedId ? findUnit(units, selectedId) : null;

  const focusRow = (unitId: string | null): void => {
    window.requestAnimationFrame(() => {
      regionRef.current?.querySelector<HTMLElement>(`[data-unit-row="${unitId}"]`)?.focus();
    });
  };

  const closePanel = (): void => {
    const rowId = selectedId;
    setChosenId(null);
    window.requestAnimationFrame(() => {
      regionRef.current?.querySelector<HTMLElement>(`[data-unit-row="${rowId}"]`)?.focus();
    });
  };

  const onRegionKeyDown = (event: React.KeyboardEvent): void => {
    if (event.key === 'Escape' && selected) closePanel();
  };

  return {
    expandedIds,
    toggleExpand: (unitId) => setExpandedIds((previous) => toggled(previous, unitId)),
    expand: (unitId) => setExpandedIds((previous) => new Set(previous).add(unitId)),
    selectedId,
    selected,
    select: setChosenId,
    closePanel,
    regionRef,
    onRegionKeyDown,
    focusRow,
  };
};

/** A dialog's own expand state, copied from the page tree when it opens, so the page tree is left as it was. */
export const useDialogExpand = (
  open: boolean,
  pageExpandedIds: Set<string>
): { expandedIds: Set<string>; toggleExpand: (unitId: string) => void } => {
  const [expandedIds, setExpandedIds] = React.useState<Set<string>>(pageExpandedIds);

  React.useEffect(() => {
    if (open) setExpandedIds(new Set(pageExpandedIds));
    // Only on opening: later page changes must not reset what was opened in the picker.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return { expandedIds, toggleExpand: (unitId) => setExpandedIds((previous) => toggled(previous, unitId)) };
};
